/* ═══════════════════════════════════════════════════════════════════════════
   Bogey's Mix — landing MOTION layer (pairs with landing-motion.css)

   One requestAnimationFrame loop drives every animated piece, and each piece
   only runs while it is on screen. Nothing here touches the offer/claim logic
   in landing.js — it only *watches* #opclaimmsg to light up the route when a
   zip is claimed.

   Pieces
     · intro              pinned scene: giant type → colour iris → ingredients drop in, giant words slam
     · delivery           pinned scene: route scrubs with scroll; background wipes at Reston and D.C.
     · claim              stamp lands when the card scrolls in; route lights up on a successful claim
     · sign               giant 'No tricks, all licks.' slides in above the final CTA
     · ticker             two marquee bands that speed up with scroll velocity
     · crowd              "4,000 dogs" counts up while a dot grid fills and ripples
     · headings           words rise through a mask
     · price / stat / steps  count-ups, one-dot-per-month, stopwatch, scroll timeline
     · final CTA          drifting mist blobs
   Reduced motion: every piece is rendered once, in its final frame. No loops.
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';
  var root = document.querySelector('.bm-landing');
  if (!root) return;
  var doc = document.documentElement;
  var reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  window.__bmMotion = true;
  doc.classList.add('bm-mo');
  if (reduce) doc.classList.add('bm-still');
  var hasIO = 'IntersectionObserver' in window;

  /* ── helpers ─────────────────────────────────────────────────────────── */
  function $(s, r) { return (r || root).querySelector(s); }
  function $$(s, r) { return [].slice.call((r || root).querySelectorAll(s)); }
  function clamp(x, a, b) { a = a === undefined ? 0 : a; b = b === undefined ? 1 : b; return x < a ? a : x > b ? b : x; }
  function lerp(a, b, t) { return a + (b - a) * t; }
  var E = {
    outCubic: function (x) { return 1 - Math.pow(1 - x, 3); },
    inOutCubic: function (x) { return x < .5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2; },
    outBack: function (x) { var c1 = 1.70158, c3 = c1 + 1; return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2); },
    outExpo: function (x) { return x >= 1 ? 1 : 1 - Math.pow(2, -10 * x); }
  };
  function quad(a, c, b, t) { var u = 1 - t; return u * u * a + 2 * u * t * c + t * t * b; }

  /* ── tiny scheduler: one rAF loop, modules run only while visible ────── */
  var mods = [], raf = null, lastTs = 0;
  function loop(ts) {
    var dt = lastTs ? Math.min(64, ts - lastTs) : 16;
    lastTs = ts;
    var any = false;
    for (var i = 0; i < mods.length; i++) {
      if (mods[i].vis) { any = true; mods[i].update(ts, dt); }
    }
    raf = any ? window.requestAnimationFrame(loop) : null;
  }
  function kick() { if (raf === null) { lastTs = 0; raf = window.requestAnimationFrame(loop); } }
  function register(el, mod, margin) {
    mod.vis = false;
    mods.push(mod);
    if (reduce) { mod.update(0, 16, true); return; }         /* one still frame */
    if (!hasIO) { mod.vis = true; kick(); return; }
    new IntersectionObserver(function (en) {
      mod.vis = en[0].isIntersecting;
      if (mod.vis) kick();
    }, { rootMargin: margin || '120px 0px' }).observe(el);
  }
  function onceVisible(el, fn, threshold) {
    if (reduce || !hasIO) { fn(true); return; }
    var io = new IntersectionObserver(function (en) {
      if (en[0].isIntersecting) { io.disconnect(); fn(false); }
    }, { threshold: threshold === undefined ? .35 : threshold });
    io.observe(el);
  }
  function animate(dur, step, done) {           /* finite 0→1 tween */
    var t0 = null;
    function f(ts) {
      if (t0 === null) t0 = ts;
      var k = clamp((ts - t0) / dur);
      step(k);
      if (k < 1) window.requestAnimationFrame(f); else if (done) done();
    }
    window.requestAnimationFrame(f);
  }

  /* ── shared: scroll progress through a pinned runway (0 → 1) ───────────── */
  function runwayP(run, pin) {
    var r = run.getBoundingClientRect(), top = parseFloat(window.getComputedStyle(pin).top) || 0;
    var travel = r.height - pin.offsetHeight;
    return travel > 1 ? clamp((top - r.top) / travel) : 0;
  }
  function px(n) { return n.toFixed(1) + 'px'; }
  function setHH() {                       /* height of the page's own logo header → keeps bottom items above the fold at load */
    var h = document.querySelector('.lp-head');
    if (h && h.offsetHeight) root.style.setProperty('--hh', (h.offsetHeight + 4) + 'px');
  }
  setHH();

  /* ═══ INTRO — giant type → colour iris → ingredients drop into the bowl ═══ */
  (function () {
    var run = $('#mhintro');
    if (!run) return;
    var pin = $('#mipin'), stage = $('#mistage'), h1 = $('.mi-h1'), iris = $('.mi-iris'), bowl = $('.mi-bowl'),
        words = $$('.mw', pin), lines = $$('.ln', h1), discs = $$('.mh-ing img', stage),
        chips = $$('.floatchip', stage), cv = $('.mh-fx', stage), cue = $('.mi-scroll');
    var ctx = cv.getContext('2d');

    /* headline: wrap every letter so it can slam in */
    var label = h1.textContent.replace(/\s+/g, ' ').trim(), gi = 0;
    lines.forEach(function (ln) {
      var txt = ln.textContent; ln.textContent = '';
      for (var k = 0; k < txt.length; k++) {
        var sp = document.createElement('span');
        sp.className = 'ch'; sp.setAttribute('aria-hidden', 'true');
        sp.style.setProperty('--i', gi++);
        sp.style.setProperty('--rot', ((gi % 2 ? -1 : 1) * (8 + (gi * 7) % 14)) + 'deg');
        sp.textContent = txt.charAt(k);
        ln.appendChild(sp);
      }
    });
    h1.setAttribute('aria-label', label);
    h1.classList.add('split');
    if (reduce) { h1.classList.add('landed'); return; }
    window.setTimeout(function () {
      h1.classList.add('landed');
      window.setInterval(function () {                       /* ripple every few seconds while the type is on screen */
        if (document.hidden || psI > .08) return;
        h1.classList.remove('wave'); void h1.offsetWidth; h1.classList.add('wave');
        window.setTimeout(function () { h1.classList.remove('wave'); }, 1500);
      }, 6500);
    }, 1000 + gi * 52);

    var W = 0, H = 0, dpr = 1, bx = 0, by = 0, bw = 0, bh = 0, D = 100, starts = [], homes = [];
    var psI = 0, springX = 0, springV = 0, landed = [false, false, false, false], parts = [];
    var COL = ['#FBF807', '#00A8FF', '#FFFFFF', '#B3DDF6', '#FBF807'];
    var TL = .30, STEP = .105, SPAN = .21;                    /* ingredient timeline */
    var WT = [.50, .605, .71, .815, .90];                      /* when each giant word lands */
    var dark = false;

    function fitWords() {
      var box = words[0].parentNode, cw = box.clientWidth * .92, ch = box.clientHeight;
      words.forEach(function (w) {
        w.style.fontSize = '100px';
        var wd = w.getBoundingClientRect().width || w.scrollWidth || 1, two = !!w.querySelector('br');
        w.style.fontSize = Math.min(100 * cw / wd, ch * (two ? .5 : .86), 210).toFixed(1) + 'px';
      });
    }
    function layout() {
      setHH();
      W = stage.clientWidth; H = stage.clientHeight;
      dpr = Math.min(2, window.devicePixelRatio || 1);
      cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
      var prev = bowl.style.transform; bowl.style.transform = 'none';
      var sr = stage.getBoundingClientRect(), br = bowl.getBoundingClientRect();
      bowl.style.transform = prev;
      bw = br.width; bh = br.height;
      bx = br.left - sr.left + bw * .5; by = br.top - sr.top + bh * .42;
      var R = Math.hypot(Math.max(bx, W - bx), Math.max(by, H - by)) + 6, S = R * 2;
      iris.style.cssText = 'width:' + px(S) + ';height:' + px(S) + ';left:' + px(bx - R) + ';top:' + px(by - R) + ';margin:0';
      D = clamp(Math.min(W, H * .62) * .3, 84, 150);
      discs.forEach(function (d) { d.style.width = D + 'px'; });
      starts = [[-.14 * W, .06 * H], [1.14 * W, .14 * H], [1.16 * W, .74 * H], [-.16 * W, .66 * H]];
      homes = chips.map(function (c) {
        return [bx - (c.offsetLeft + c.offsetWidth / 2), by - (c.offsetTop + c.offsetHeight / 2)];
      });
      fitWords();
    }
    function burst(x, y, n, sp) {
      for (var i = 0; i < n; i++) {
        var a = -Math.PI / 2 + (Math.random() - .5) * 2.8, v = (80 + Math.random() * 190) * sp;
        parts.push({ x: x, y: y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: 1, r: 3 + Math.random() * 5,
          c: COL[(Math.random() * COL.length) | 0], sq: Math.random() < .4 });
      }
    }

    function frame(ts, dt) {
      var t = ts / 1000, i, target = runwayP(run, pin);
      psI += (target - psI) * (1 - Math.exp(-dt / 80));
      if (Math.abs(target - psI) < .0015) psI = target;
      var p = psI;

      /* colour iris expands from the bowl */
      var e = E.inOutCubic(clamp((p - .10) / .24));
      iris.style.transform = 'scale(' + e.toFixed(4) + ')';
      var d2 = e > .42;
      if (d2 !== dark) { dark = d2; pin.classList.toggle('dark', d2); }
      if (cue) cue.classList.toggle('gone', p > .03);
      /* type slides away in a staircase as the iris arrives */
      var tp = E.inOutCubic(clamp((p - .06) / .26));
      for (i = 0; i < lines.length; i++) {
        lines[i].style.transform = 'translateX(' + ((i % 2 ? 1 : -1) * tp * 70).toFixed(1) + 'vw) rotate(' + ((i % 2 ? 1 : -1) * tp * 5).toFixed(2) + 'deg)';
      }

      /* bowl spring — every landing presses it down, it rebounds */
      springV += (-260 * springX - 13 * springV) * dt / 1000;
      springX += springV * dt / 1000;
      var idle = Math.sin(t * 1.4) * .004, lift = E.outCubic(clamp((p - .08) / .3)), rise = 1 - E.outCubic(clamp(p / .16));
      bowl.style.transform = 'translateY(' + (springX * 6 - lift * H * .03 + rise * bh * .36).toFixed(2) + 'px) scale(' +
        (1 + lift * .05 + springX * .035 + idle).toFixed(4) + ',' + (1 + lift * .05 - springX * .06 - idle).toFixed(4) + ')';

      /* ingredients arc in from the corners, one per beat */
      for (i = 0; i < discs.length; i++) {
        var s = starts[i], k = E.inOutCubic(clamp((p - TL - i * STEP) / SPAN));
        var cx = (s[0] + bx) / 2 + (i % 2 ? .14 : -.14) * W, cy = Math.min(s[1], by) - H * .22;
        var wob = 1 - k;
        var x = quad(s[0], cx, bx, k) + Math.sin(t * 1.7 + i * 1.9) * 8 * wob;
        var y = quad(s[1], cy, by - bh * .02, k) + Math.cos(t * 1.5 + i * 2.3) * 8 * wob;
        var sc = lerp(1.2, .5, k), rot = (1 - k) * (i % 2 ? -1 : 1) * 200 + Math.sin(t * 2 + i) * 6 * wob;
        var op = k <= 0 ? 0 : k > .86 ? clamp(1 - (k - .86) / .12) : 1;
        var dsc = discs[i];
        dsc.style.opacity = op.toFixed(3);
        dsc.style.transform = 'translate(' + px(x - D / 2) + ',' + px(y - D / 2) + ') rotate(' + rot.toFixed(1) + 'deg) scale(' + sc.toFixed(3) + ')';
        var lnd = k >= .985;
        if (lnd && !landed[i]) { springV += 9; burst(bx + (Math.random() - .5) * bw * .2, by - bh * .05, 14, 1.1); }
        landed[i] = lnd;
      }

      /* giant words slam in behind the bowl, one per ingredient */
      for (i = 0; i < words.length; i++) {
        var w = words[i], a0 = WT[i], nx = WT[i + 1];
        var inn = E.outBack(clamp((p - a0) / .04)), out = nx ? clamp((p - (nx - .025)) / .025) : 0;
        var o = clamp((p - a0) / .02) * (1 - out);
        w.style.opacity = o.toFixed(3);
        w.style.transform = 'translateY(' + ((1 - inn) * 26 - out * 26).toFixed(1) + '%) scale(' + (1.6 - .6 * inn - out * .15).toFixed(3) + ') rotate(' + ((1 - inn) * (i % 2 ? 6 : -6)).toFixed(1) + 'deg)';
      }

      /* trust chips pop out of the bowl */
      for (i = 0; i < chips.length; i++) {
        var q = E.outBack(clamp((p - .80 - i * .02) / .09)), c = chips[i];
        c.style.opacity = clamp(q * 3).toFixed(3);
        c.style.transform = 'translate(' + (homes[i][0] * (1 - q)).toFixed(1) + 'px,' + (homes[i][1] * (1 - q)).toFixed(1) + 'px) scale(' + Math.max(q, 0).toFixed(3) + ')';
      }

      /* canvas: steam + confetti */
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      var steam = clamp((p - .82) / .08);
      if (steam > 0) {
        ctx.lineCap = 'round'; ctx.lineWidth = 6;
        for (var wv = 0; wv < 3; wv++) {
          var x0 = bx + (wv - 1) * bw * .16, y0 = by - bh * .14, px0 = x0, py0 = y0;
          for (var j = 1; j <= 16; j++) {
            var yy = y0 - j * 7, xx = x0 + Math.sin(t * 1.9 + j * .42 + wv * 2.1) * (2 + j * .7);
            ctx.strokeStyle = 'rgba(255,255,255,' + (steam * .75 * (1 - j / 17)).toFixed(3) + ')';
            ctx.beginPath(); ctx.moveTo(px0, py0); ctx.lineTo(xx, yy); ctx.stroke();
            px0 = xx; py0 = yy;
          }
        }
      }
      for (i = parts.length - 1; i >= 0; i--) {
        var pt = parts[i];
        pt.vy += 420 * dt / 1000; pt.x += pt.vx * dt / 1000; pt.y += pt.vy * dt / 1000; pt.life -= dt / 950;
        if (pt.life <= 0) { parts.splice(i, 1); continue; }
        ctx.globalAlpha = clamp(pt.life * 1.6); ctx.fillStyle = pt.c;
        if (pt.sq) ctx.fillRect(pt.x - pt.r, pt.y - pt.r, pt.r * 2, pt.r * 2);
        else { ctx.beginPath(); ctx.arc(pt.x, pt.y, pt.r, 0, 6.283); ctx.fill(); }
      }
      ctx.globalAlpha = 1;
    }

    var rz = null;
    function relayout() { window.clearTimeout(rz); rz = window.setTimeout(layout, 120); }
    window.addEventListener('resize', relayout, { passive: true });
    if (!bowl.complete) bowl.addEventListener('load', layout);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(layout);
    layout();
    psI = runwayP(run, pin);
    register(run, { update: frame }, '0px');
  })();

  /* ═══ DELIVERY — scrubbed route, colour wipes at Reston and D.C. ═════════ */
  var routeApi = { setClaimed: function () {} };
  (function () {
    var run = $('#mhrun');
    if (!run) return;
    var pin = $('#mrpin'), svg = $('#mhroute'), fill = $('.rt-fill', svg), tok = $('.tok', svg), sts = $$('.st', svg),
        ml = $$('.ml', pin), wp = $('.wp', pin), wy = $('.wy', pin);
    var L = fill.getTotalLength();
    fill.style.strokeDasharray = L;
    var stL = sts.map(function (st) {
      var x = +st.getAttribute('data-x'), y = +st.getAttribute('data-y'), best = 0, bd = 1e9;
      for (var d = 0; d <= L; d += 2) {
        var p = fill.getPointAtLength(d), dd = (p.x - x) * (p.x - x) + (p.y - y) * (p.y - y);
        if (dd < bd) { bd = dd; best = d; }
      }
      return best;
    });
    routeApi.setClaimed = function (v) { run.classList.toggle('is-claimed', v); };
    function paint(prog, t) {
      fill.style.strokeDashoffset = L * (1 - prog);
      var pt = fill.getPointAtLength(prog * L), b = prog >= .999 ? 1 + .1 * Math.sin(t * 5) : 1;
      tok.setAttribute('transform', 'translate(' + pt.x.toFixed(2) + ' ' + pt.y.toFixed(2) + ') scale(' + b.toFixed(3) + ')');
      for (var i = 0; i < sts.length; i++) {
        var hit = prog * L >= stL[i] - 1;
        if (hit !== sts[i]._hit) { sts[i]._hit = hit; sts[i].classList.toggle('hit', hit); }
      }
    }
    if (reduce) { paint(1, 0); return; }

    var W = 0, H = 0, S = 0, c1 = [0, 0], c2 = [0, 0], s2 = stL[2] / L, s4 = stL[4] / L;
    function toPin(st) {                                  /* a station → pixel position inside the pin */
      var r = pin.getBoundingClientRect(), sr = st.getBoundingClientRect();
      return [sr.left + sr.width / 2 - r.left, sr.top + sr.height / 2 - r.top];
    }
    function layout() {
      W = pin.clientWidth; H = pin.clientHeight;
      S = Math.hypot(W, H) * 2.05;
      c1 = toPin(sts[2]); c2 = toPin(sts[4]);
      [[wp, c1], [wy, c2]].forEach(function (a) {
        a[0].style.width = a[0].style.height = px(S);
        a[0].style.left = px(a[1][0] - S / 2); a[0].style.top = px(a[1][1] - S / 2);
      });
    }
    var ps = 0, cls = 'c1';
    function frame(ts, dt) {
      var t = ts / 1000, target = clamp((runwayP(run, pin) - .05) / .82);
      ps += (target - ps) * (1 - Math.exp(-dt / 70));
      if (Math.abs(target - ps) < .0015) ps = target;
      var prog = E.inOutCubic(ps) * .5 + ps * .5;           /* mostly linear so stops feel evenly paced */
      paint(prog, t);
      var e1 = E.inOutCubic(clamp((prog - s2 + .05) / .16)), e2 = E.inOutCubic(clamp((prog - s4 + .07) / .09));
      wp.style.transform = 'scale(' + (e1 * .0 + e1).toFixed(4) + ')';
      wy.style.transform = 'scale(' + e2.toFixed(4) + ')';
      var nc = e2 > .5 ? 'c3' : e1 > .5 ? 'c2' : 'c1';
      if (nc !== cls) { pin.classList.remove(cls); pin.classList.add(nc); cls = nc; }
      var entered = run.getBoundingClientRect().top < window.innerHeight * .8;   /* headline starts as the scene arrives, not after the first scroll */
      pin.classList.toggle('in', entered);
      pin.classList.toggle('rolling', prog > .04);
      ml[0].classList.toggle('on', entered || prog > .005);
      ml[1].classList.toggle('on', entered || prog >= s2 - .02);
      ml[2].classList.toggle('on', prog >= s4 - .04);
    }
    window.addEventListener('resize', function () { window.setTimeout(layout, 120); }, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(layout);
    layout();
    register(run, { update: frame }, '0px');
    ps = clamp((runwayP(run, pin) - .05) / .82);
  })();

  /* claim success → light up the route + stamp (landing.js owns the claim itself) */
  (function () {
    var msg = $('#opclaimmsg'), card = $('#offerpanel');
    if (!card) return;
    onceVisible(card, function () { card.classList.add('in'); }, .3);
    if (!msg || !window.MutationObserver) return;
    new MutationObserver(function () {
      var ok = msg.classList.contains('ok');
      card.classList.toggle('is-claimed', ok);
      routeApi.setClaimed(ok);
    }).observe(msg, { attributes: true, attributeFilter: ['class'] });
  })();

  /* ═══ SIGN — giant "No tricks, all licks." slides in with scroll ═══════════ */
  (function () {
    var sign = $('#mhsign');
    if (!sign || reduce) return;
    var a = $('.s1>span', sign), b = $('.s2>span', sign);
    register(sign, {
      update: function () {
        var r = sign.getBoundingClientRect(), vh = window.innerHeight;
        var k = E.outCubic(clamp((vh * .92 - r.top) / (vh * .5)));
        a.style.transform = 'translateX(' + ((1 - k) * -110).toFixed(1) + 'vw)';
        b.style.transform = 'translateX(' + ((1 - k) * 110).toFixed(1) + 'vw)';
      }
    }, '10px 0px');
  })();

  /* ═══ TICKER — speeds up with scroll velocity ══════════════════════════ */
  (function () {
    var tick = $('.mh-tick');
    if (!tick || reduce) return;
    var trks = $$('.trk', tick), anims = [];
    var lastY = window.scrollY, vs = 0, rate = 1;
    function grab() {
      if (anims.length || !trks[0].getAnimations) return;
      trks.forEach(function (t) { var a = t.getAnimations()[0]; if (a) anims.push(a); });
    }
    register(tick, {
      update: function (ts, dt) {
        grab();
        var y = window.scrollY, v = Math.abs(y - lastY) / Math.max(dt, 1) * 1000;
        lastY = y;
        vs += (v - vs) * .12;
        rate += ((1 + clamp(vs / 700, 0, 5)) - rate) * .08;
        for (var i = 0; i < anims.length; i++) anims[i].playbackRate = rate;
      }
    });
  })();

  /* ═══ CROWD — 4,000 dots that flip, wave by wave, to spell "4,000" ═══════ */
  (function () {
    var num = $('#mhnum');
    if (!num) return;
    var COLS = 125, ROWS = 32, N = COLS * ROWS;                    /* 125 × 32 = 4,000 dots */
    var label = num.textContent.trim() || '4,000';
    var cv = document.createElement('canvas');
    cv.className = 'mh-dots';
    cv.setAttribute('aria-hidden', 'true');
    var sr = document.createElement('span');
    sr.className = 'sr'; sr.textContent = label;
    num.textContent = ''; num.appendChild(sr); num.appendChild(cv); num.classList.add('dots');
    var ctx = cv.getContext('2d');
    var dots = [], W = 0, H = 0, dpr = 1, ps = 0, target = 0, built = false;
    var COL = { pop: '#00A8FF', ylw: '#FBF807', blu: '#B3DDF6', mist: 'rgba(0,42,50,.07)' };

    function build() {
      /* rasterise the number into the dot grid */
      var o = document.createElement('canvas'); o.width = COLS; o.height = ROWS;
      var oc = o.getContext('2d');
      var fam = '"Arial Black","Helvetica Neue",Arial,sans-serif';  /* heavy, clean numerals read best at 32 rows */
      oc.font = '400 100px ' + fam;
      var w1 = oc.measureText(label).width || 300;
      var fs = Math.min(100 * (COLS - 10) / w1, ROWS * 1.25);
      oc.font = '400 ' + fs + 'px ' + fam;
      oc.textAlign = 'center'; oc.textBaseline = 'alphabetic';
      oc.fillStyle = '#000';
      oc.lineJoin = 'round'; oc.lineWidth = 2.4; oc.strokeStyle = '#000';
      oc.strokeText(label, COLS / 2, ROWS / 2 + fs * .36);
      oc.fillText(label, COLS / 2, ROWS / 2 + fs * .36);
      var px = oc.getImageData(0, 0, COLS, ROWS).data;
      function rnd(n) { var h = Math.sin(n * 127.1) * 43758.5453; return h - Math.floor(h); }
      dots = [];
      for (var r = 0; r < ROWS; r++) for (var c = 0; c < COLS; c++) {
        var on = px[(r * COLS + c) * 4 + 3] > 110;
        var hsh = Math.sin(c * 127.1 + r * 311.7) * 43758.5453; var v = (hsh - Math.floor(hsh)) * 11;
        dots.push({
          c: c, r: r, on: on,
          col: on ? (v < 1.1 ? COL.ylw : v < 2.2 ? COL.blu : COL.pop) : COL.mist,
          d: (c / COLS) * .5 + ((c * 53 + r * 29) % 17) / 17 * .18,   /* left→right wave, a little scatter */
          sx: rnd(c * 3.1 + r * 7.7), sy: rnd(c * 9.3 + r * 1.9), ph: rnd(c * 5.5 + r * 2.3) * 6.28, om: .7 + rnd(c * 1.7 + r * 4.1) * 1.4,
          k: Math.floor(rnd(c * 8.8 + r * 6.6) * 5),
          sc: PAL[Math.floor(rnd(c * 2.2 + r * 3.3) * 5)]
        });
      }
      built = true;
    }
    function size() {
      var w = cv.clientWidth || 300;
      dpr = Math.min(2, window.devicePixelRatio || 1);
      W = w; H = w * ROWS / COLS;
      cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
      cv.style.height = H + 'px';
    }
    var cap = document.querySelector('.mh-cap'), capFinal = cap ? cap.textContent : '';
    var FLAPS = 3, PAL = [COL.pop, COL.ylw, COL.blu, COL.pop, COL.ylw];
    /* q: 0 → 1 over ~2.6 s once the section arrives. Before that the dots float, scattered. */
    function draw(q, ts) {
      if (!built) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      var pitch = W / COLS, rad = pitch * .38, doneN = 0, t = (ts || 0) / 1000;
      var sweep = (q >= 1 && ts) ? ((t * .42) % 2.4) * COLS - 12 : -99;              /* idle shimmer once landed */
      for (var i = 0; i < dots.length; i++) {
        var d = dots[i];
        var gx = (d.c + .5) * pitch, gy = (d.r + .5) * pitch;
        var s = d.d / .68 * .5, g = clamp((q - s) / .45);                             /* this dot's own flip window */
        var x, y, sx = 1, sc = 1, col, alpha = 1;
        if (g <= 0) {                                                                  /* floating, scattered */
          x = d.sx * W + Math.sin(t * d.om + d.ph) * pitch * 5;
          y = d.sy * H + Math.cos(t * d.om * .8 + d.ph) * pitch * 4;
          sx = .55 + .45 * Math.abs(Math.sin(t * d.om * 1.3 + d.ph));                  /* tumbling as they drift */
          col = d.sc; sc = 1.15; alpha = .95;
        } else {
          var m = E.outCubic(g);
          var fx = d.sx * W + Math.sin(t * d.om + d.ph) * pitch * 5, fy = d.sy * H + Math.cos(t * d.om * .8 + d.ph) * pitch * 4;
          x = lerp(fx, gx, m); y = lerp(fy, gy, m);
          if (g < 1) {                                                                 /* split-flap: rapid flips through colours */
            var ph = g * FLAPS, k = Math.floor(ph), fr = ph - k;
            sx = Math.abs(Math.cos(fr * Math.PI));
            col = k >= FLAPS - 1 && fr > .5 ? d.col : PAL[(k + d.k) % PAL.length];
            if (!d.on && k >= FLAPS - 1) col = fr > .5 ? COL.mist : 'rgba(0,42,50,.14)';
            sc = 1 + Math.sin(g * Math.PI) * .35;
          } else {
            col = d.col; doneN++;
            if (sweep > -50 && d.on) {                                                 /* shimmer band */
              var dx = Math.abs(d.c - sweep - d.r * .35);
              if (dx < 4) { var kk = 1 - dx / 4; sc *= 1 + .5 * kk; if (kk > .35) col = COL.ylw; }
            }
          }
        }
        ctx.fillStyle = col;
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.ellipse(x, y, Math.max(.001, rad * sc * sx), rad * sc, 0, 0, 6.2832);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      if (cap) cap.textContent = q >= 1 ? capFinal : doneN.toLocaleString('en-US') + ' dots. Every one a very good dog.';
    }
    var q = 0, t0 = null, started = false, DUR = 2600;
    function start() { if (!started) { started = true; t0 = null; } }
    function init() { size(); build(); draw(reduce || root.classList.contains('bm-still') ? 1 : q, 0); }
    init();
    if (window.document.fonts && document.fonts.ready) document.fonts.ready.then(function () { build(); draw(q, 0); });
    window.addEventListener('resize', function () { size(); draw(q, 0); });
    if (reduce) { draw(1, 0); return; }
    if (hasIO) {
      new IntersectionObserver(function (en) {
        var e = en[0];
        if (e.isIntersecting) start();
        else if (e.boundingClientRect.top > 0) { started = false; q = 0; }      /* scrolled back above it → arm a replay */
      }, { threshold: .5 }).observe(cv);
    } else start();
    register(cv, {
      update: function (ts) {
        if (started) {
          if (t0 === null) t0 = ts;
          q = clamp((ts - t0) / DUR);
        }
        draw(q, ts);
      }
    }, '200px 0px');
  })();

  /* ═══ REELS — prev/next arrows + mouse drag for desktop (touch keeps native swipe) ═══ */
  (function () {
    var track = $('#reelstrack'), sec = $('#reels');
    if (!track || !sec) return;
    var fine = window.matchMedia && window.matchMedia('(hover:hover) and (pointer:fine)').matches;
    var mk = function (dir) {
      var b = document.createElement('button');
      b.type = 'button'; b.className = 'mh-rnav ' + dir;
      b.setAttribute('aria-label', dir === 'prev' ? 'Previous videos' : 'Next videos');
      b.innerHTML = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="' + (dir === 'prev' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7') + '"/></svg>';
      sec.appendChild(b); return b;
    };
    var prev = mk('prev'), next = mk('next');
    function step() { var c = track.querySelector('.reel'); return c ? c.getBoundingClientRect().width + 14 : 300; }
    function go(dir) { track.scrollBy({ left: dir * step(), behavior: reduce ? 'auto' : 'smooth' }); }
    prev.addEventListener('click', function () { go(-1); });
    next.addEventListener('click', function () { go(1); });
    function sync() {
      var over = track.scrollWidth > track.clientWidth + 6;
      sec.classList.toggle('has-nav', over);
      if (!over) return;
      var top = track.offsetTop + track.offsetHeight / 2;
      prev.style.top = next.style.top = top + 'px';
      prev.style.left = Math.max(8, track.offsetLeft + 6) + 'px';
      next.style.left = 'auto'; next.style.right = Math.max(8, sec.clientWidth - track.offsetLeft - track.offsetWidth + 6) + 'px';
      prev.disabled = track.scrollLeft <= 4;
      next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
    }
    track.addEventListener('scroll', function () { window.requestAnimationFrame(sync); }, { passive: true });
    window.addEventListener('resize', sync);
    if (window.ResizeObserver) new ResizeObserver(sync).observe(track);       /* Instagram embeds resize themselves after load */
    sync(); window.setTimeout(sync, 1200); window.setTimeout(sync, 3500);
    /* click-and-drag with a mouse */
    if (fine) {
      var down = false, x0 = 0, s0 = 0;
      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true; x0 = e.clientX; s0 = track.scrollLeft;
        track.classList.add('dragging');
      });
      window.addEventListener('pointermove', function (e) { if (down) track.scrollLeft = s0 - (e.clientX - x0); });
      window.addEventListener('pointerup', function () { if (down) { down = false; track.classList.remove('dragging'); } });
    }
  })();

  /* ═══ HOMEPAGE SECTIONS — gentle photo parallax + Bogey Bucks count-up ═══ */
  (function () {
    var pics = $$('.mh-meet-pic, .mh-split-pic');
    var cnt = $('.mh-cnt');
    if (cnt && !reduce) {
      var to = parseInt(cnt.getAttribute('data-to'), 10) || 0;
      onceVisible(cnt, function () {
        animate(1400, function (k) { cnt.textContent = Math.round(E.outCubic(k) * to).toLocaleString('en-US'); });
      }, .6);
    }
    if (reduce || !pics.length || !('translate' in document.documentElement.style)) return;
    pics.forEach(function (pc) {
      register(pc.parentNode, {
        update: function () {
          var vh = window.innerHeight, r = pc.getBoundingClientRect();
          pc.style.translate = '0 ' + ((r.top + r.height / 2 - vh / 2) * -.05).toFixed(1) + 'px';
        }
      }, '200px 0px');
    });
  })();

  /* ═══ CONTACT — "Send us a woof" (same endpoint as the homepage form) ═══ */
  (function () {
    var form = $('#mhcontact');
    if (!form) return;
    var btn = $('#mhc-send'), st = $('#mhc-status');
    var f = { name: $('#mhc-name'), email: $('#mhc-email'), phone: $('#mhc-phone'), subject: $('#mhc-subject'), msg: $('#mhc-msg') };
    function say(txt, cls) { st.textContent = txt; st.className = 'mh-form-msg' + (cls ? ' ' + cls : ''); }
    function bad(el, on) { el.classList.toggle('bad', !!on); }
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var name = f.name.value.trim(), email = f.email.value.trim(), phone = f.phone.value.replace(/\D/g, ''), msg = f.msg.value.trim(), sub = f.subject.value.trim();
      var okEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      bad(f.name, !name); bad(f.email, !okEmail); bad(f.msg, !msg); bad(f.phone, phone && phone.length !== 10);
      if (!name || !email || !msg) { say('Please fill in your name, email and message.', 'err'); return; }
      if (!okEmail) { say('Please enter a valid email address.', 'err'); return; }
      if (phone && phone.length !== 10) { say('Please enter a valid 10-digit phone number, or leave it blank.', 'err'); return; }
      btn.disabled = true; say('Sending…');
      fetch('https://isnjz3kdxk.execute-api.us-east-1.amazonaws.com/prod/contact-us', {
        method: 'POST',
        headers: { 'x-api-key': 'gmkxBXr02VaPXOhdX79Ywa9RkG7TvViO1FGUSd58', 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name, email: email, phone: phone || null, message: sub ? sub + ' — ' + msg : msg })
      }).then(function (r) { return r.json(); }).then(function (d) {
        if (d && d.statusCode === 200) {
          say("Thank you for contacting us! We've received your message and will get back to you shortly.", 'ok');
          form.reset();
        } else { say('Failed to send your message. Please try again later.', 'err'); }
      }).catch(function () {
        say('Something went wrong sending your message. Please try again later.', 'err');
      }).then(function () { btn.disabled = false; });
    });
  })();

  /* ═══ 32 MONTHS — one dot per month, in step with the count-up ═════════ */
  (function () {
    var box = $('#mhmonths');
    if (!box) return;
    var dots = [];
    for (var i = 0; i < 32; i++) { var d = document.createElement('i'); box.appendChild(d); dots.push(d); }
    function paint(p) {
      var n = Math.round(p * 32);
      for (var i = 0; i < 32; i++) {
        var on = i < n;
        if (on !== dots[i]._on) { dots[i]._on = on; dots[i].classList.toggle('on', on); }
      }
    }
    if (reduce) { paint(1); return; }
    onceVisible(box, function () {
      window.setTimeout(function () { animate(1300, function (k) { paint(E.outCubic(k)); }); }, 150);
    }, .6);
  })();

  /* ═══ STOPWATCH + steps timeline ═══════════════════════════════════════ */
  (function () {
    var w = $('#mhwatch'), n = $('#mhsec'), arc = w && $('.arc', w);
    if (w && arc && n && !reduce) {
      n.textContent = '0';
      onceVisible(w, function () {
        animate(1900, function (k) {
          var p = E.inOutCubic(k);
          arc.style.strokeDashoffset = (100 * (1 - p)).toFixed(2);
          n.textContent = Math.round(p * 60);
        });
      }, .6);
    } else if (arc) { arc.style.strokeDashoffset = 0; }
    var steps = $('.steps');
    if (steps) {
      var upd = function () {
        var r = steps.getBoundingClientRect(), vh = window.innerHeight;
        var sp = clamp((vh * .72 - r.top) / r.height);
        steps.style.setProperty('--sp', sp.toFixed(3));
        var lis = steps.children;
        for (var i = 0; i < lis.length; i++) {
          lis[i].classList.toggle('lit', sp > i / 3 + .04);
          lis[i].classList.toggle('done', sp > (i + 1) / 3 + .02);
        }
      };
      if (reduce) { steps.style.setProperty('--sp', 1); for (var q = 0; q < steps.children.length; q++) steps.children[q].classList.add('lit', 'done'); }
      else {
        upd();
        var tk = false;
        window.addEventListener('scroll', function () {
          if (tk) return; tk = true;
          window.requestAnimationFrame(function () { tk = false; upd(); });
        }, { passive: true });
      }
    }
  })();

  /* ═══ PRICES — count up when the comparison lands ══════════════════════ */
  (function () {
    var cmp = $('.compare');
    if (!cmp || reduce) return;
    var prices = $$('.crow-price', cmp).map(function (el) {
      var m = el.textContent.match(/^(\D*)(\d+)(.*)$/);
      return m ? { el: el, pre: m[1], n: +m[2], post: m[3] } : null;
    }).filter(Boolean);
    prices.forEach(function (p) { p.el.textContent = p.pre + '0' + p.post; });
    onceVisible(cmp, function () {
      window.setTimeout(function () {
        animate(1400, function (k) {
          var e = E.outCubic(k);
          prices.forEach(function (p) { p.el.textContent = p.pre + Math.round(p.n * e) + p.post; });
        });
      }, 250);
    }, .35);
  })();

  /* ═══ HEADINGS — words rise through a mask ═════════════════════════════ */
  (function () {
    if (reduce) return;
    var hs = $$('h2, h3.subhead').filter(function (h) { return !h.hasAttribute('data-nosplit'); });
    hs.forEach(function (h) {
      var idx = 0;
      (function walk(node) {
        [].slice.call(node.childNodes).forEach(function (n) {
          if (n.nodeType === 3) {
            var frag = document.createDocumentFragment();
            n.nodeValue.split(/(\s+)/).forEach(function (part) {
              if (!part) return;
              if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
              var w = document.createElement('span'); w.className = 'w';
              var wi = document.createElement('span'); wi.className = 'wi';
              wi.style.setProperty('--wi', idx++); wi.textContent = part;
              w.appendChild(wi); frag.appendChild(w);
            });
            n.parentNode.replaceChild(frag, n);
          } else if (n.nodeType === 1 && n.tagName !== 'BR') walk(n);
        });
      })(h);
      onceVisible(h, function () { h.classList.add('sp-in'); }, .3);
    });
    $$('h2[data-nosplit]').forEach(function (h) { h.classList.add('sp-in'); });
  })();

  /* ═══ FINAL CTA — drifting mist blobs ══════════════════════════════════ */
  (function () {
    var box = $('#mhfgoo');
    if (!box) return;
    var els = $$('i', box), W = 0, H = 0, ph = [.2, 1.9, 3.4, 5.1, 2.6, 4.3, .9];
    function measure() { W = box.clientWidth; H = box.clientHeight; }
    measure();
    window.addEventListener('resize', measure, { passive: true });
    /* cloud banks along the top and bottom edges: x, y (fractions), radius (× width) */
    var base = [[.04, .0, .17], [.36, -.01, .13], [.70, .0, .18], [1.0, .02, .12],
                [.10, 1.0, .19], [.50, 1.02, .14], [.90, 1.0, .20]];
    function blob(el, x, y, r) {
      el.style.transform = 'translate(' + (x - 50).toFixed(1) + 'px,' + (y - 50).toFixed(1) + 'px) scale(' + (r / 50).toFixed(3) + ')';
    }
    var inited = false;
    register(box, {
      update: function (ts) {
        if (!inited) { els.forEach(function (e) { e.style.cssText = 'left:0;top:0;width:100px;height:100px;margin:0;aspect-ratio:auto;display:block'; }); inited = true; }
        var t = ts / 1000;
        for (var i = 0; i < els.length; i++) {
          var b = base[i % base.length];
          var x = W * b[0] + Math.sin(t * .28 + ph[i]) * W * .07;
          var y = H * b[1] + Math.cos(t * .33 + ph[i] * 1.3) * W * .03;
          var r = W * b[2] * (1 + .14 * Math.sin(t * .5 + ph[i]));
          blob(els[i], x, y, r);
        }
      }
    }, '200px 0px');
  })();
})();

/* Site nav: sticks to the top, slides away on scroll down, slides back on scroll up (same as the index header). */
(function () {
  var nav = document.querySelector('.mh-nav');
  if (!nav) return;
  var burger = nav.querySelector('.mh-nav-burger');
  var last = window.pageYOffset || 0, ticking = false;
  function update() {
    ticking = false;
    var y = window.pageYOffset || 0, d = y - last;
    nav.classList.toggle('is-stuck', y > 4);
    if (y < 80 || (burger && burger.open)) nav.classList.remove('is-hidden');
    else if (d > 6) nav.classList.add('is-hidden');
    else if (d < -4) nav.classList.remove('is-hidden');
    if (Math.abs(d) > 4 || y < 80) last = y;
  }
  window.addEventListener('scroll', function () {
    if (!ticking) { ticking = true; requestAnimationFrame(update); }
  }, { passive: true });
  update();
})();
